/**
 * A bus in service, frame by frame: where along its way it has got, the
 * stop it is coming to, and how it should drive for it. The traffic code
 * (`three/traffic.ts`) drives it as it drives any car, among the others;
 * this says, besides, where to stop, how long to stand there, and how fast
 * to go to keep to the timetable.
 *
 * - **Stops.** It pulls in toward the kerb for its next stop and stops with
 *   its front door at it. It stands there at least `MIN_DWELL`, and until
 *   its departure time if it is early, then signals and pulls out. The
 *   departure is read afresh each frame: the times are Västtrafik's best
 *   estimates, polled every half minute, and one that moves while it stands
 *   moves its leaving with it. There are no positions to go by between
 *   polls, only the timetable. A stop laid on its way where the one before
 *   was is called at once, not again.
 * - **The timetable.** Between stops it drives no faster than gets it to the
 *   next one at its time, but never slower than `SLOWEST` of the road's
 *   speed: early, it would rather wait at the stop than crawl. Late, it
 *   drives the road's speed.
 * - **Stops behind it.** One it has got past without stopping, spawned past
 *   it or pushed by, is let go.
 *
 * Pure: no DOM, no three.js.
 */

import type { TransitTrip, TripLayout } from './trip.js';
import { alongNear } from './trip.js';

export interface BusService {
  trip: TransitTrip;
  layout: TripLayout;
  /** How far along its way it is, metres. */
  along: number;
  /** The next stop to call at. Past the last, it is out of service. */
  served: number;
  /** Seconds left standing at the stop it is at, as things stand; 0 when not at one. */
  dwell: number;
  /** When it got to the stop it stands at, ms; NaN when not at one. */
  arrivedAt: number;
  /** Most it waits at a stop for its time, and at its first stop, seconds. */
  most: number;
  first: number;
  /** Seconds left signalling, pulling out. */
  pullOut: number;
}

/** Least standing at a stop, seconds: the doors open, people on and off. */
export const MIN_DWELL = 7;
/** Most waited at a stop for its time, seconds; the first stop waits longer for a bus there early. */
const MOST_DWELL = 90;
const FIRST_DWELL = 300;
/** Its front this far past the stop's point, metres: the front door at it. */
const STOP_PAST = 1;
/** Slowed for a stop from this far off, metres. */
const SEES_STOP = 70;
/** Never slower than this share of the road's speed for the timetable. */
const SLOWEST = 0.55;
/** A stop this far behind it, metres, is one it has got past. */
const PASSED = 6;
/** A stop this close along its way to the one it has just left is the same place, metres. */
const SAME_PLACE = 15;

export function startService(trip: TransitTrip, layout: TripLayout, along: number, most = MOST_DWELL, first = FIRST_DWELL): BusService {
  let served = 0;
  while (served < layout.stopAlong.length && layout.stopAlong[served] < along - PASSED) served++;
  return { trip, layout, along, served, dwell: 0, arrivedAt: NaN, most, first, pullOut: 0 };
}

export interface ServiceStep {
  /**
   * From its front to where it must stop, metres: Infinity if nowhere. For
   * the Intelligent Driver Model, which keeps its least gap short of what it
   * stops for, add that least gap (`toStop`): else it halts that far short of
   * the stop, too far to be at it, and stands there for good.
   */
  stopGap: number;
  /** The speed the timetable has it at, m/s. */
  pace: number;
  /** Over toward the kerb, for a stop. */
  kerb: boolean;
  /** Its indicator on, pulling out. */
  signal: boolean;
  /** Past its last stop: out of service. */
  done: boolean;
}

/**
 * One frame for a bus at scene (x, z), at `speed`, its half length, `road`
 * the speed it would otherwise drive, `now` the time, ms.
 */
export function serve(s: BusService, x: number, z: number, speed: number, halfLength: number, road: number, dt: number, now: number): ServiceStep {
  const stops = s.trip.stops, at = s.layout.stopAlong;
  s.along = alongNear(s.layout, x, z, s.along).along;
  if (s.pullOut > 0) s.pullOut = Math.max(0, s.pullOut - dt);
  if (Number.isFinite(s.arrivedAt)) {
    s.dwell = standing(s, now);
    if (s.dwell > 0) return { stopGap: 0.05, pace: 0, kerb: true, signal: s.dwell < 2, done: false };
    // Away: past this stop, and any laid where it is.
    s.dwell = 0;
    s.arrivedAt = NaN;
    const left = at[s.served];
    s.served++;
    while (s.served < stops.length && at[s.served] - left < SAME_PLACE) s.served++;
    s.pullOut = 2.5;
    return { stopGap: Infinity, pace: road, kerb: false, signal: true, done: false };
  }
  while (s.served < stops.length && at[s.served] < s.along - PASSED) s.served++;
  if (s.served >= stops.length) return { stopGap: Infinity, pace: road, kerb: false, signal: false, done: true };
  const stop = stops[s.served];
  const gap = at[s.served] + STOP_PAST - (s.along + halfLength);
  if (gap < 1.5 && speed < 0.6) {
    // There: standing until its time, and the least a stop takes.
    s.arrivedAt = now;
    s.dwell = standing(s, now);
    return { stopGap: 0.05, pace: 0, kerb: true, signal: false, done: false };
  }
  // Pacing to arrive at its time, not crawling to it.
  const seconds = (stop.arrive - now) / 1000, metres = at[s.served] - s.along;
  const pace = seconds > 1 ? Math.max(road * SLOWEST, Math.min(road, (metres / seconds) * 1.15)) : road;
  return {
    stopGap: gap < SEES_STOP ? Math.max(0.05, gap) : Infinity,
    pace,
    kerb: gap < 25,
    signal: s.pullOut > 0,
    done: false,
  };
}

/**
 * Seconds still to stand at the stop it is at: the least a stop takes, and
 * until its departure as now known, but no more than its most from when it
 * got there.
 */
function standing(s: BusService, now: number): number {
  const stop = s.trip.stops[s.served];
  const most = s.arrivedAt + (s.served === 0 ? s.first : s.most) * 1000;
  const until = Math.min(most, stop === undefined ? now : stop.depart);
  return Math.max(s.arrivedAt / 1000 + MIN_DWELL - now / 1000, (until - now) / 1000, 0);
}

/** How far short of its stop a vehicle the Intelligent Driver Model drives stops, metres: well within reach of it. */
const SHORT = 0.5;

/** A step's stop gap as a gap for the Intelligent Driver Model keeping `minGap`: it comes to rest just short of the stop. */
export function toStop(stopGap: number, minGap: number): number {
  return Number.isFinite(stopGap) ? stopGap + Math.max(0, minGap - SHORT) : stopGap;
}
